import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  HOVER_SCALE,
  MAX_TILT_DEG,
  TILT_PERSPECTIVE_PX,
} from './readyStep.constants';

interface TiltCardProps {
  children: ReactNode;
  enabled: boolean;
  /** Source image used as the glare layer's CSS mask so the highlight
   *  is clipped to the pack silhouette, not to the surrounding
   *  aspect-ratio rectangle. Pass the same `object-contain` PNG that
   *  the children renders. */
  glareMaskUrl: string | null;
}

/**
 * Subtle hover-tilt + glare over its children. Only active when
 * `enabled` is true (slot at rest); during spin / unveil / snap the
 * wrapper passes the children through untransformed so no interactive
 * effect competes with the wheel animation. Touch devices never fire
 * mouseMove so the tilt naturally stays at rest on mobile.
 */
export function TiltCard({ children, enabled, glareMaskUrl }: TiltCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [hovering, setHovering] = useState(false);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, px: 0.5, py: 0.5 });

  // Reset state when the card stops being interactive (Shuffle press
  // mid-hover). Without this, the next time it goes interactive the
  // card pops back to whatever angle the cursor was at before.
  useEffect(() => {
    if (!enabled) {
      setHovering(false);
      setTilt({ rx: 0, ry: 0, px: 0.5, py: 0.5 });
    }
  }, [enabled]);

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    // Y movement → X-axis rotation (tilt towards / away from camera).
    // X movement → Y-axis rotation (tilt left / right).
    const rx = -(py - 0.5) * 2 * MAX_TILT_DEG;
    const ry = (px - 0.5) * 2 * MAX_TILT_DEG;
    setTilt({ rx, ry, px, py });
  };

  const handleEnter = () => {
    if (!enabled) return;
    setHovering(true);
  };

  const handleLeave = () => {
    setHovering(false);
    setTilt({ rx: 0, ry: 0, px: 0.5, py: 0.5 });
  };

  const transform = enabled
    ? `perspective(${TILT_PERSPECTIVE_PX}px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg) scale(${hovering ? HOVER_SCALE : 1})`
    : 'none';

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="absolute inset-0"
      style={{
        transform,
        transformStyle: 'preserve-3d',
        transition: 'transform 200ms ease-out',
        // `will-change` keeps the GPU layer ready so the first
        // mouseMove after entering doesn't kick off a fresh repaint.
        willChange: enabled ? 'transform' : 'auto',
      }}
    >
      {children}
      {/* Glare — radial highlight that tracks the cursor. Clipped to
          the pack silhouette via `mask-image: url(packImageUrl)` so
          the highlight stops at the bag's edge instead of bleeding
          into the transparent corners of the object-contain rect.
          `mix-blend-mode: soft-light` keeps it readable on both
          light and dark pack art without washing colours out. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(circle at ${tilt.px * 100}% ${tilt.py * 100}%, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 25%, transparent 55%)`,
          opacity: enabled && hovering ? 1 : 0,
          mixBlendMode: 'soft-light',
          transition: 'opacity 150ms ease-out',
          ...(glareMaskUrl
            ? {
                maskImage: `url("${glareMaskUrl}")`,
                maskSize: 'contain',
                maskPosition: 'center',
                maskRepeat: 'no-repeat',
                WebkitMaskImage: `url("${glareMaskUrl}")`,
                WebkitMaskSize: 'contain',
                WebkitMaskPosition: 'center',
                WebkitMaskRepeat: 'no-repeat',
              }
            : null),
        }}
      />
    </div>
  );
}
